import { useState } from 'react';
import { Link, Navigate } from '@tanstack/react-router';
import { useRhinoContext } from '@rhino-project/core';
import { useSignUpAction } from '@rhino-project/core/queries';
import { useHarborlightSession } from '../hooks/useHarborlightSession';

// Ported 1:1 from the source repository (client/src/pages/RegisterPage.jsx).
// Standalone page (no app shell). Wired to Rhino devise-token-auth sign-up so
// new guests register through the framework's supported auth flow.
const RegisterPage = () => {
  const { user } = useRhinoContext();
  const { roleHomePath } = useHarborlightSession();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const signUp = useSignUpAction();
  const busy = signUp.isPending;

  if (user) return <Navigate to={roleHomePath} replace />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      await signUp.mutateAsync({
        name,
        email,
        password,
        password_confirmation: password
      } as never);
    } catch {
      setError('That email is already registered.');
    }
  }

  return (
    <div className="auth-layout">
      <div className="auth-visual fade-up">
        <div>
          <div className="display-title display-4 mb-2">Join room stay 1</div>
          <p className="fs-5 mb-0 opacity-75">
            Create a guest account to browse rooms and book stays.
          </p>
        </div>
      </div>
      <div className="auth-form-wrap">
        <div className="surface-panel p-4 p-md-5 w-100 auth-card fade-up-delay">
          <h1 className="display-title h2 mb-1">Create account</h1>
          <p className="text-secondary mb-4">
            Guests register here. Staff accounts are provisioned by admin.
          </p>
          {error && <div className="alert alert-danger">{error}</div>}
          <form onSubmit={onSubmit} className="d-grid gap-3">
            <div>
              <label className="form-label" htmlFor="name">
                Full name
              </label>
              <input
                id="name"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="form-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-copper btn-lg"
              disabled={busy}
            >
              {busy ? 'Creating…' : 'Create guest account'}
            </button>
          </form>
          <p className="mt-4 mb-0">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
